import { useState } from 'react';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Loader2, Paintbrush, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RetouchRequestModal({ photo, isOpen, onClose }) {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requestId, setRequestId] = useState(null);
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_name || !formData.client_email || !formData.description) {
      toast.error(language === 'pl' ? 'Wypełnij wszystkie pola' : 'Fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/public/photos/${photo.id}/retouch-request`, {
        photo_id: photo.id,
        ...formData
      });
      setRequestId(response.data.request_id);
      setSubmitted(true);
      toast.success(language === 'pl' 
        ? 'Prośba o retusz została wysłana!' 
        : 'Retouch request submitted!'
      );
    } catch (error) {
      toast.error(language === 'pl' ? 'Wystąpił błąd' : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setRequestId(null);
    setFormData({ client_name: '', client_email: '', description: '' });
    onClose();
  };

  if (!photo) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paintbrush className="w-5 h-5" />
            {language === 'pl' ? 'Zamów obróbkę autorską' : 'Request Custom Retouch'}
          </DialogTitle>
          <DialogDescription>
            {language === 'pl' 
              ? 'Fotograf przygotuje spersonalizowaną wersję tego zdjęcia' 
              : 'The photographer will prepare a personalized version of this photo'
            }
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-lg font-semibold mb-2">
              {language === 'pl' ? 'Prośba wysłana!' : 'Request Sent!'}
            </h3>
            <p className="text-muted-foreground text-sm mb-4">
              {language === 'pl' 
                ? 'Fotograf skontaktuje się z wyceną na podany email.' 
                : 'The photographer will contact you with a quote via email.'
              }
            </p>
            <p className="text-xs text-muted-foreground">
              {language === 'pl' ? 'ID żądania:' : 'Request ID:'} {requestId}
            </p>
            <Button className="mt-6" onClick={handleClose}>
              {language === 'pl' ? 'Zamknij' : 'Close'}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Photo preview */}
            <div className="flex gap-4 p-3 bg-secondary/50 rounded-sm">
              <img
                src={photo.thumbnail_url || photo.url}
                alt={photo.original_filename}
                className="w-20 h-20 object-cover rounded-sm"
              />
              <div>
                <p className="font-medium text-sm">{photo.original_filename}</p>
                <p className="text-xs text-muted-foreground">
                  {language === 'pl' ? 'Wybrane zdjęcie do obróbki' : 'Selected photo for retouch'}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_name">
                {language === 'pl' ? 'Twoje imię' : 'Your name'}
              </Label>
              <Input
                id="client_name"
                value={formData.client_name}
                onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                placeholder={language === 'pl' ? 'Jan Kowalski' : 'John Doe'}
                className="h-11"
                data-testid="retouch-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_email">
                {language === 'pl' ? 'Twój email' : 'Your email'}
              </Label>
              <Input
                id="client_email"
                type="email"
                value={formData.client_email}
                onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                placeholder="email@example.com"
                className="h-11"
                data-testid="retouch-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">
                {language === 'pl' ? 'Opis oczekiwanej obróbki' : 'Describe expected retouch'}
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={language === 'pl' 
                  ? 'Np. Proszę o usunięcie tła, korekcję kolorów, retusz skóry...' 
                  : 'E.g. Please remove background, color correction, skin retouch...'
                }
                rows={4}
                data-testid="retouch-description-input"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={handleClose}>
                {language === 'pl' ? 'Anuluj' : 'Cancel'}
              </Button>
              <Button type="submit" className="flex-1" disabled={loading} data-testid="retouch-submit-btn">
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Paintbrush className="w-4 h-4 mr-2" />
                )}
                {language === 'pl' ? 'Wyślij prośbę' : 'Send Request'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
